import { ReactNode } from 'react';
import Select from 'react-select';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import classnames from 'src/util/classnames';
import { Icon } from 'src/components/Icon';
import { Button, Input } from 'src/components/ui';
import { SegmentedControl } from 'src/components/forms';
import CustomBadge from '../../CustomBadge';
import { periodLabel } from '../reportingCadence';
import { dueDayLabel, yearTypeShortLabel } from './scheduleLabels';
import { DueOn, MONTH_DAY_DUE_ONS, PERIODS, Period, ScheduleRow as Row, YEAR_TYPES, YearType, changePeriod, showsYearType } from './scheduleRowRules';
import { ScheduleOption } from './useScheduleOptions';

/** Shared by the header and every row so the columns line up; below the field's `@4xl` container width (56rem: fixed tracks 10+8+13+2rem, four gaps 3rem, frequency control ~17rem, row padding 1rem) each row stacks as a card. */
export const ROW_GRID = '@4xl:grid @4xl:grid-cols-[minmax(10rem,1fr)_auto_8rem_13rem_2rem] @4xl:gap-3 @4xl:items-center';

export function ScheduleTableHeader({ readOnly }: { readOnly: boolean }) {
  return (
    <div
      className={classnames(
        'hidden px-2 pb-1 text-muted small font-semibold text-center border-0 border-b border-solid border-gray-200 dark:border-gray-600',
        ROW_GRID
      )}
    >
      <span>
        <Trans>Schedule</Trans>
      </span>
      <span>
        <Trans>Frequency</Trans>
      </span>
      <span>
        <Trans>Year type</Trans>
      </span>
      <span>
        <Trans>Due day</Trans>
      </span>
      {/* <span><Trans>Required</Trans></span> */}
      {!readOnly && <span />}
    </div>
  );
}

/** `centered` lines content up under its centered header in the table layout; inputs that fill their column don't need it. */
function Cell({ label, centered, children }: { label: string; centered?: boolean; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="@4xl:hidden text-muted small w-24 shrink-0">{label}</span>
      <div className={classnames('min-w-0 flex-1', centered && '@4xl:flex @4xl:justify-center @4xl:text-center')}>{children}</div>
    </div>
  );
}

const EMPTY_CELL = <span className="text-muted">—</span>;

function FollowingFridayChip() {
  return (
    <CustomBadge color="secondary" pill className="inline-flex items-center gap-1">
      <Icon icon="LockKey" size={12} />
      {dueDayLabel('following-friday')}
    </CustomBadge>
  );
}

export type ScheduleRowProps = {
  id: string;
  row: Row;
  options: ScheduleOption[];
  loadingOptions: boolean;
  errors: string[];
  onChange?: (row: Row) => void;
  onRemove?: () => void;
};

/** One schedule requirement. Without `onChange` it renders as plain text (view mode). */
export function ScheduleRow({ id, row, options, loadingOptions, errors, onChange, onRemove }: ScheduleRowProps) {
  const readOnly = !onChange;
  const scheduleLabel = options.find(option => option.value === row.schedule)?.label ?? row.schedule;
  const selectedSchedule = row.schedule ? { value: row.schedule, label: scheduleLabel ?? row.schedule } : null;
  const update = (patch: Partial<Row>) => onChange?.({ ...row, ...patch });

  const scheduleCell = readOnly ? (
    <span className="font-semibold">{scheduleLabel || EMPTY_CELL}</span>
  ) : (
    <Select
      inputId={`${id}_schedule`}
      aria-label={t`Schedule`}
      className="autocomplete-container"
      classNamePrefix="autocomplete"
      menuPortalTarget={document.body}
      styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
      options={options}
      value={selectedSchedule}
      isLoading={loadingOptions}
      placeholder={t`Select a schedule`}
      onChange={option => update({ schedule: option?.value })}
    />
  );

  const periodCell = readOnly ? (
    periodLabel(row.period) || EMPTY_CELL
  ) : (
    <SegmentedControl<Period>
      id={`${id}_period`}
      label={t`Frequency`}
      size="xs"
      value={row.period as Period}
      options={PERIODS.map(period => ({ value: period, label: periodLabel(period) }))}
      onChange={period => onChange?.(changePeriod(row, period))}
    />
  );

  // Weekly and monthly are always saved as CY (see normalizeRow), so the year type is shown but not editable.
  const yearTypeCell = !showsYearType(row.period) ? (
    <span className="text-muted">{yearTypeShortLabel('CY')}</span>
  ) : readOnly ? (
    yearTypeShortLabel(row.yearType)
  ) : (
    <Input
      type="select"
      bsSize="sm"
      aria-label={t`Year type`}
      value={row.yearType ?? ''}
      onChange={e => update({ yearType: e.target.value as YearType })}
    >
      {!row.yearType && <option value="" />}
      {row.yearType && !YEAR_TYPES.includes(row.yearType as YearType) && <option value={row.yearType}>{row.yearType}</option>}
      {YEAR_TYPES.map(yearType => (
        <option key={yearType} value={yearType}>
          {yearTypeShortLabel(yearType)}
        </option>
      ))}
    </Input>
  );

  const dueOnCell =
    row.period === 'weekly' ? (
      <FollowingFridayChip />
    ) : readOnly ? (
      dueDayLabel(row.dueOn) || EMPTY_CELL
    ) : (
      <Input type="select" bsSize="sm" aria-label={t`Due day`} value={row.dueOn ?? ''} onChange={e => update({ dueOn: e.target.value as DueOn })}>
        {!row.dueOn && <option value="" />}
        {row.dueOn && !MONTH_DAY_DUE_ONS.includes(row.dueOn) && <option value={row.dueOn}>{row.dueOn}</option>}
        {MONTH_DAY_DUE_ONS.map(dueOn => (
          <option key={dueOn} value={dueOn}>
            {dueDayLabel(dueOn)}
          </option>
        ))}
      </Input>
    );

  // "Required" is not used in practice yet; the toggle stays here, hidden, until it does something.
  // const requiredCell = <Toggle checked={!!row.isRequired} onChange={readOnly ? undefined : isRequired => update({ isRequired })} title={t`Required`} />;

  return (
    <div
      className={classnames(
        'flex flex-col gap-2 p-2 mb-2 rounded-md border border-solid @4xl:mb-0 @4xl:rounded-none @4xl:border-0 @4xl:border-b',
        errors.length ? 'border-danger' : 'border-gray-200 dark:border-gray-600'
      )}
      data-testid={id}
    >
      <div className={classnames('flex flex-col gap-2', ROW_GRID)}>
        <Cell label={t`Schedule`}>{scheduleCell}</Cell>
        <Cell label={t`Frequency`} centered>
          {periodCell}
        </Cell>
        <Cell label={t`Year type`} centered={readOnly || !showsYearType(row.period)}>
          {yearTypeCell}
        </Cell>
        <Cell label={t`Due day`} centered={readOnly || row.period === 'weekly'}>
          {dueOnCell}
        </Cell>
        {/* <Cell label={t`Required`}>{requiredCell}</Cell> */}
        {!readOnly && (
          <div className="flex justify-end">
            <Button color="clear" size="sm" className="text-danger" onClick={onRemove} aria-label={t`Remove schedule`} title={t`Remove schedule`}>
              <Icon icon="Trash" />
            </Button>
          </div>
        )}
      </div>
      {errors.map(error => (
        <div key={error} className="text-danger small flex items-start gap-1">
          <Icon icon="Warning" className="w-3 h-3 mt-0.5 shrink-0" />
          {error}
        </div>
      ))}
    </div>
  );
}
